import type React from 'react';
import { Box, CircularProgress, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { formatAdminNumber } from '../../../../utils/numberFormat';
import { useAdminVideoJob } from '../api';
import { JobStatusChip } from './JobStatusChip';

const JsonBlock: React.FC<{ title: string; value: unknown }> = ({ title, value }) => (
  <Box>
    <Typography variant="subtitle2" className="font-semibold text-gray-700 dark:text-gray-300">
      {title}
    </Typography>
    <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded border bg-gray-50 p-3 font-mono text-xs dark:border-slate-800 dark:bg-slate-900">
      {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
    </pre>
  </Box>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box className="min-w-0">
    <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
      {label}
    </Typography>
    <Typography variant="body2" className="break-all text-gray-900 dark:text-slate-100">
      {value ?? '—'}
    </Typography>
  </Box>
);

export const VideoJobDetailDrawer: React.FC<{ jobId: string | null; onClose: () => void }> = ({
  jobId,
  onClose,
}) => {
  const { data: job, isLoading } = useAdminVideoJob(jobId);

  return (
    <Drawer anchor="right" open={Boolean(jobId)} onClose={onClose}>
      <Box className="w-screen max-w-[640px] space-y-4 p-5">
        <Box className="flex items-center justify-between gap-2">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Video job
          </Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <X size={18} />
          </IconButton>
        </Box>
        {isLoading || !job ? (
          <Box className="flex justify-center py-10">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box className="flex items-center gap-2">
              <JobStatusChip status={job.status} />
              <Typography variant="body2" className="font-mono text-gray-600 dark:text-slate-300">
                {job.jobId}
              </Typography>
            </Box>
            <Box className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Operation" value={job.capabilityCode} />
              <Field label="User" value={job.userEmail ?? job.userId} />
              <Field label="Model" value={`${job.costTier ?? ''} · ${job.modelCode ?? ''}`} />
              <Field label="Provider" value={job.providerCode} />
              <Field label="Request ID" value={job.requestId} />
              <Field label="Workflow instance" value={job.workflowInstanceId} />
              <Field
                label="Credits (estimated / charged)"
                value={`${formatAdminNumber(job.estimatedTokens ?? 0)} / ${formatAdminNumber(job.chargedTokens ?? 0)}`}
              />
              <Field label="API key" value={job.apiKeyId} />
              <Field label="Created" value={new Date(job.createdAt).toLocaleString()} />
              <Field
                label="Completed"
                value={job.completedAt ? new Date(job.completedAt).toLocaleString() : null}
              />
              <Field
                label="Cancel requested"
                value={
                  job.cancelRequestedAt ? new Date(job.cancelRequestedAt).toLocaleString() : null
                }
              />
              <Field
                label="Output expires"
                value={job.expiresAt ? new Date(job.expiresAt).toLocaleString() : null}
              />
            </Box>
            <Divider />
            <JsonBlock title="Input" value={job.input} />
            <JsonBlock title="Output" value={job.output} />
            <JsonBlock title="Error" value={job.error} />
            <JsonBlock title="Gateway usage" value={job.usage} />
          </>
        )}
      </Box>
    </Drawer>
  );
};
